import { ListKeyManager, ListKeyManagerOption } from '@angular/cdk/a11y';
import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import * as Constants from '@emi/shared/constants';
import { SkuSearchService as GetSkus } from '@emi/shared/data-access';
import { Category, CheckboxTreeNode, ConfigureData, DateRange, ExtractionMutated, Filters, IndustryCategory, SearchOption } from '@emi/shared/models';
import {
    CategoryState,
    CheckboxState,
    DataExtractionState,
    addConfigureDataSelection,
    amendAttributeSelections,
    amendAttributeTypeSelections,
    amendBrandSelections,
    amendPromotionSelections,
    amendRetailerSelections,
    amendRetailerTypesSelections,
    amendSupplierSelections,
    clearCheckboxes,
    clearFilters,
    clearSelections,
    getCheckboxState,
    getCountries,
    getExchangeRates,
    getFilterCategoryState,
    getIsLoadingSelections,
    getReloadedExtraction,
    getSelectedConfigureData,
    getSelectedFilters,
    isLoadingSelections,
    updateDefaultDateRange,
} from '@emi/shared/state/filters';
import { SkuSearchState, getSkuCount, getSkuCountSuccess, getSkuSearchResults, selectSkuSearchState, setCurrency } from '@emi/shared/state/sku-search';
import { getSkuMetadataState, SkuMetadataState } from '@emi/shared/state/sku-metadata';
import { Store, select } from '@ngrx/store';
import { GenericModalComponent } from 'libs/shared/ui/src/lib/generic-modal/generic-modal.component';
import { Subscription } from 'rxjs';
import { TabModelBuilder } from './filter-tabs-modelbuilder';
import { FilterTab } from './filter-tabs.model';
import * as _ from 'lodash';
import * as moment from 'moment';

@Component({
    selector: 'via-filter-tabs',
    templateUrl: './filter-tabs.component.html',
    styleUrls: ['./filter-tabs.component.scss'],
})
export class FilterTabsComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChildren('tabButton')
    public tabButtonElements: QueryList<ListKeyManagerOption>;

    @Input()
    public isOverlay = false;

    public loading = false;
    public showFilters = true;
    public disableApplyButton = false;
    public displaySkuCount = false;
    public skuCount = 0;
    public searchTerm = '';
    public searchOption: SearchOption;
    public nextButtonIsHidden = false;
    public skuCountMessage = '';
    public filterTabsTooltip = Constants.Tooltips.FILTER_TABS;
    public resetFiltersButtonTooltip = Constants.Tooltips.RESET_FILTERS;
    public filterTabs: FilterTab[];
    public applyClicked = new EventEmitter();
    public cancelClicked = new EventEmitter();
    public resetButtonDisabled = true;
    public activeTabId: number;
    public keyManager: ListKeyManager<any>;
    public previouslySelectedFilters = 0;
    public modalType = Constants.LocalStorageValues.TEMPORARY_SELECTION_MODAL;
    public categories: Array<Category>;
    public disableAllFilters: boolean;
    public tabsTitle: string;
    public includeSearchPhrase: boolean | undefined;
    public selectedCurrency: string;
    public configureData = new ConfigureData();

    public sortByTypes = [
        { value: 'totalRetailers', label: 'Total Retailers' },
        { value: 'totalSkuSize', label: 'Total SKU Size' },
    ];

    private selectedFilters: Filters;
    private skuSearchSubscription: Subscription;
    private temporarySelections: Array<CheckboxTreeNode>;

    private skuCountCalledForReload: boolean = false;

    constructor(
        private readonly dataExtractionStore: Store<DataExtractionState>,
        private readonly skuSearchStore: Store<SkuSearchState>,
        private readonly checkboxStore: Store<CheckboxState>,
        private readonly categoryStore: Store<CategoryState>,
        private readonly skuMetadataStore: Store<SkuMetadataState>,
        public dialog: MatDialog,
        private getSkusService: GetSkus
    ) {}

    public ngOnInit(): void {
        this.skuMetadataStore.pipe(select(getSkuMetadataState)).subscribe((metadata) => {
            const maxDate = metadata.lastUpdated ? metadata.lastUpdated : moment().utc().subtract(Constants.DatePicker.DEFAULT_MAX_DATE_DAYS_AGO, 'days').toDate();
            const defaultDateRange = DateRange.getDefaultDateRange(maxDate);
            this.dataExtractionStore.dispatch(updateDefaultDateRange({ defaultDateRange }));
        }).unsubscribe();
        
        this.dataExtractionStore.pipe(select(getSelectedFilters)).subscribe((selectedFilters) => {
            this.filterTabs = TabModelBuilder.build(selectedFilters, this.isOverlay);
            this.resetButtonDisabled = this.disableResetButton(selectedFilters);
            this.selectedFilters = selectedFilters;

            this.displaySkuCount = this.shouldDisplaySkuCount(selectedFilters);
            this.checkAllFiltersReloadedAsPerExtraction(selectedFilters);
        });

        this.dataExtractionStore.pipe(select(getIsLoadingSelections)).subscribe((isLoading) => (this.loading = isLoading));

        this.checkboxStore.pipe(select(getCheckboxState)).subscribe((checkboxState) => {
            this.temporarySelections = checkboxState.checkboxes;
        });

        this.categoryStore.pipe(select(getFilterCategoryState)).subscribe((x) => {
            this.categories = x.categories;
        });

        this.skuSearchSubscription = this.skuSearchStore.pipe(select(selectSkuSearchState)).subscribe((state) => {
            this.disableAllFilters = state.searchTerm.length > 0 && !(state.skuResults.length > 0);
            this.skuCount = state.skuCount;
            this.searchTerm = state.searchTerm;
            this.searchOption = state.searchOption;
            this.includeSearchPhrase = state.includeSearchPhrase;
            this.nextButtonIsHidden = this.isNextButtonHidden(state.skuCount);
            this.selectedCurrency = state.currency;
        });

        // if (this.shouldDisplaySkuCount(this.selectedFilters)) {
        //     this.skuSearchStore.dispatch(getSkuCount());
        // }

        if (this.isOverlay) {
            this.tabsTitle = 'Refine Search';
            this.toggleFilters(false);
        } else {
            this.tabsTitle = 'Filters';
            this.activeTabId = this.filterTabs[0].id;
        }
    }

    public ngAfterViewInit(): void {
        this.keyManager = new ListKeyManager(this.tabButtonElements).withWrap();
    }

    public applyFilters(): void {
        this.disableApplyButton = false;
        this.applyClicked.emit();
        this.getSkusService.updateSearchFilterText(false);

        if (this.shouldDisplaySkuCount(this.selectedFilters)) {
            this.skuSearchStore.dispatch(getSkuCount());
            this.nextButtonIsHidden = this.isNextButtonHidden(this.skuCount);
        }

        this.previouslySelectedFilters = this.filterTabs.find((x) => x.id === this.activeTabId)?.selectedCount ?? 0;
        this.amendFilterSelections(this.selectedFilters);
        if (this.isOverlay) {
            this.skuSearchStore.dispatch(
                getSkuSearchResults({
                    searchTerm: this.searchTerm,
                    searchOption: this.searchOption,
                    includeSearchPhrase: this.includeSearchPhrase,
                })
            );
        }
    }

    public cancelChanges(): void {
        this.disableApplyButton = false;
        this.cancelClicked.emit();
    }

    public disableResetButton(selectedFilters: Filters): boolean {
        let disableResetButton = true;
        disableResetButton = this.checkForPriceDateAndSKUSizeFilters(selectedFilters);
        for (const [key, val] of Object.entries(selectedFilters)) {
            if (val && val.length > 0) {
                disableResetButton = false;
            }
        }
        return disableResetButton;
    }

    public checkForPriceDateAndSKUSizeFilters(selectedFilters: Filters): boolean {
        if (
            (selectedFilters.dateRange?.endDate && selectedFilters.dateRange.endDate) ||
            (selectedFilters.firstSeenPriceRange?.min && selectedFilters.firstSeenPriceRange?.max) ||
            (selectedFilters.lastSeenPriceRange?.min && selectedFilters.lastSeenPriceRange.max) ||
            (selectedFilters.volumeRange?.min && selectedFilters.volumeRange.max) ||
            (selectedFilters.weightRange?.min && selectedFilters.weightRange.max) ||
            (selectedFilters.countRange?.min && selectedFilters.countRange.max)
        ) {
            return false;
        } else {
            return true;
        }
    }

    public navigateToCategoriesTab(): void {
        if (!this.isOverlay) {
            this.dataExtractionStore.dispatch(clearCheckboxes());
            this.activeTabId = this.filterTabs[1].id;
        }
    }

    public resetFilters(): void {
        this.previouslySelectedFilters = 0;
        this.dataExtractionStore.dispatch(clearFilters());
        this.dataExtractionStore.dispatch(clearCheckboxes());
        this.dataExtractionStore.dispatch(clearSelections());
        this.getSkusService.updateSearchFilterText(true);
        this.dataExtractionStore.dispatch(getCountries());

        this.dataExtractionStore.pipe(select(getSelectedConfigureData)).subscribe((x) => {
            this.configureData = x;
        });

        const configureData = { ...this.configureData, currency: this.selectedCurrency };
        this.dataExtractionStore.dispatch(addConfigureDataSelection({ configureData }));
        this.dataExtractionStore.dispatch(getExchangeRates({ currency: this.selectedCurrency }));
        this.skuSearchStore.dispatch(setCurrency({ currency: this.selectedCurrency }));

        this.skuSearchStore.dispatch(getSkuCountSuccess({ skuCount: 0 }));

        if (this.isOverlay) {
            this.skuSearchStore.dispatch(
                getSkuSearchResults({
                    searchTerm: this.searchTerm,
                    searchOption: this.searchOption,
                    includeSearchPhrase: this.includeSearchPhrase,
                })
            );
            this.toggleFilters(false);
        } else {
            this.activeTabId = this.filterTabs[0].id;
        }
    }

    public changeTab(id: number): void {
        if (id === this.activeTabId) return;

        this.previouslySelectedFilters = this.filterTabs.find((x) => x.id === this.activeTabId)?.selectedCount ?? 0;

        let selectedCount: number = 0;
        let temporaryCount: number = this.temporarySelections.length;
        let doesCountDiffer: boolean = false;

        switch (this.activeTabId) {
            case Constants.FilterDataTabIds.COUNTRIES:
                selectedCount = this.selectedFilters.countries.length;
                break;
            case Constants.FilterDataTabIds.CATEGORIES:
                const selectedCategories: Array<Category> = new Array<Category>();
                const checkboxes = this.temporarySelections?.map((category) => new IndustryCategory(category.id, category.industryCode));
                let refinedCategories = [];
                (this.getCategoryChildren(this.categories, checkboxes) ?? []).map((child) => selectedCategories.push(child));
                refinedCategories = this.getSelectedCategories(selectedCategories);
                const savedCategories = this.getSelectedCategories(this.selectedFilters.categories);

                selectedCount = refinedCategories.length === 0 ? savedCategories.length : refinedCategories.length;
                break;
            case Constants.FilterDataTabIds.SUPPLIERS:
                selectedCount = this.selectedFilters.suppliers.length;
                break;
            case Constants.FilterDataTabIds.BRANDS:
                temporaryCount = 0;
                this.selectedFilters.brands.forEach((brand) => {
                    selectedCount += brand.subBrands?.length ? brand.subBrands.length : 1;
                });
                this.temporarySelections.forEach((brand) => {
                    if (brand.children?.length || brand.children?.length !== undefined) {
                        temporaryCount += brand.children.length;
                    } else {
                        temporaryCount += 1;
                    }
                });
                break;
            case Constants.FilterDataTabIds.ATTRIBUTE_TYPES:
                selectedCount = this.selectedFilters.attributeTypes.length;
                break;
            case Constants.FilterDataTabIds.ATTRIBUTES:
                selectedCount = this.selectedFilters.attributes.length;
                break;
            case Constants.FilterDataTabIds.RETAILER_TYPES:
                selectedCount = this.selectedFilters.retailerTypes.length;
                break;
            case Constants.FilterDataTabIds.RETAILERS:
                selectedCount = this.selectedFilters.retailers.length;
                break;
            case Constants.FilterDataTabIds.PROMOTIONS:
                selectedCount = this.selectedFilters.promotions.length;
                break;
        }

        doesCountDiffer = selectedCount !== temporaryCount;

        const show = localStorage.getItem(this.modalType) ?? 'true';
        const showModal = JSON.parse(show);

        if ((this.temporarySelections.length !== this.previouslySelectedFilters || doesCountDiffer) && showModal) {
            const dialogRef = this.dialog.open(GenericModalComponent, {
                width: '340px',
                data: {
                    buttons: {
                        primary: 'Proceed',
                        secondary: 'Cancel',
                    },
                    content:
                        'Some selections have not been applied, by moving away from this screen you will lose these changes. Do you wish to proceed?',
                    iconClass: 'fa-solid fa-triangle-exclamation',
                    title: 'Warning',
                    type: this.modalType,
                    showUnderstoodCheckbox: true,
                },
            });

            dialogRef.afterClosed().subscribe((result) => {
                if (result) {
                    this.setActiveTab(id);
                }
            });
        } else {
            this.setActiveTab(id);
        }
    }

    public setActiveTab(id: number): void {
        this.activeTabId = id;
        this.previouslySelectedFilters = this.filterTabs.find((x) => x.id === id)?.selectedCount ?? 0;
        this.dataExtractionStore.dispatch(clearCheckboxes());

        if (this.isOverlay) {
            this.toggleFilters(true);
        }
    }

    public tabKeyEvents(event: KeyboardEvent): void {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            this.keyManager.onKeydown(event);

            while (this.keyManager.activeItem.nativeElement.disabled) {
                this.keyManager.onKeydown(event);
            }

            this.keyManager.activeItem.nativeElement.focus();
        }
    }

    public setFocusTab(): void {
        if (this.keyManager.activeItemIndex === -1) {
            this.keyManager.setFirstItemActive();
        }
    }

    private amendFilterSelections(filters: Filters): void {
        if (
            filters.suppliers.length > 0 ||
            filters.brands.length > 0 ||
            filters.attributeTypes.length > 0 ||
            filters.attributes.length > 0 ||
            filters.retailerTypes.length > 0 ||
            filters.retailers.length > 0 ||
            filters.promotions.length > 0
        ) {
            this.dataExtractionStore.dispatch(isLoadingSelections({ isLoading: true }));
        }

        if (filters.suppliers.length > 0) {
            this.dataExtractionStore.dispatch(amendSupplierSelections());
        }

        if (filters.brands.length > 0) {
            this.dataExtractionStore.dispatch(amendBrandSelections());
        }

        if (filters.attributeTypes.length > 0) {
            this.dataExtractionStore.dispatch(amendAttributeTypeSelections());
        }

        if (filters.attributes.length > 0) {
            this.dataExtractionStore.dispatch(amendAttributeSelections());
        }

        if (filters.retailerTypes.length > 0) {
            this.dataExtractionStore.dispatch(amendRetailerTypesSelections());
        }

        if (filters.retailers.length > 0) {
            this.dataExtractionStore.dispatch(amendRetailerSelections());
        }

        if (filters.promotions.length > 0) {
            this.dataExtractionStore.dispatch(amendPromotionSelections());
        }
    }

    private shouldDisplaySkuCount(selectedFilters: Filters): boolean {
        if (!this.isOverlay && selectedFilters.countries && selectedFilters.countries.length > 0) {
            return true;
        }
        return false;
    }

    private isNextButtonHidden(skuCount: number): boolean {
        const skuCountLimit = Constants.Application.MAX_SELECTABLE_SKUS.value;

        if (this.displaySkuCount && skuCount === 0) {
            this.skuCountMessage = 'No SKUs found for your selection. Please refine your search criteria.';
            return true;
        }

        if (skuCount <= skuCountLimit) {
            this.skuCountMessage = '';
            return false;
        }

        if (this.displaySkuCount && skuCount > skuCountLimit) {
            this.skuCountMessage = Constants.Application.MAX_SELECTABLE_SKUS.message;
        }

        return true;
    }

    public toggleFilters(showFilters: boolean): void {
        this.showFilters = showFilters;

        if (!this.showFilters) {
            this.dataExtractionStore.dispatch(clearCheckboxes());
            this.activeTabId = -1;
        }
    }

    public getActiveTabTitle(): string | undefined {
        return this.filterTabs.find((x) => x.id === this.activeTabId)?.labels?.map(l => l.title).join(', ');
    }

    private getCategoryChildren(categories: Category[], selectedCategories: IndustryCategory[]): Category[] | null {
        const relevantCategories = [];
        for (const category of categories) {
            if (
                selectedCategories.some(
                    (selectedCategory) =>
                        selectedCategory.categoryId === category.id && selectedCategory.industryCode == category.industryCode
                )
            ) {
                relevantCategories.push(category);
            } else if (category.childCategories) {
                const relevantChildren = this.getCategoryChildren(category.childCategories, selectedCategories);
                if (relevantChildren) {
                    let newCategory = { ...category, childCategories: relevantChildren };
                    relevantCategories.push(newCategory);
                }
            }
        }
        return relevantCategories.length > 0 ? relevantCategories : null;
    }

    private getSelectedCategories(categoryTree: Array<Category>, selectedCategories: Array<Category> = []): Array<Category> {
        for (const category of categoryTree) {
            if (!category.childCategories || category.childCategories.length === 0) {
                selectedCategories.push(category);
            } else {
                this.getSelectedCategories(category.childCategories, selectedCategories).sort((a, b) =>
                    a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1
                );
            }
        }
        return selectedCategories;
    }

    public ngOnDestroy(): void {
        this.dataExtractionStore.dispatch(clearCheckboxes());
        this.skuSearchSubscription.unsubscribe();
    }

    private checkAllFiltersReloadedAsPerExtraction(selectedFilters: Filters) {
        this.dataExtractionStore.select(getReloadedExtraction).subscribe((reloadedExtraction) => {
            // If extraction is not null and skuCount has not already been called, then only call came from reload selections from export
            if (reloadedExtraction && !this.skuCountCalledForReload) {
                if (this.displaySkuCount && this.compareSelectedFiltersAndExtractionFiltersForReload(selectedFilters, reloadedExtraction)) {
                    this.skuCountCalledForReload = true;
                    this.skuSearchStore.dispatch(getSkuCount());
                    this.skuSearchStore.dispatch(isLoadingSelections({ isLoading: false }));
                }
            }
        });
    }

    private compareSelectedFiltersAndExtractionFiltersForReload(selectedFilters: Filters, reloadedExtraction: ExtractionMutated): boolean {
        let allFiltersReloaded = true;

        let filtersFromReloadedExtraction: Array<string> = [];
        const dateRange: string = 'dateRange';
        const subBrandIds: string = 'subBrandIds';
        // Pushing date range explictly as the filters don't return date range because they have start date and end date for configure already
        filtersFromReloadedExtraction.push(dateRange);
        if (reloadedExtraction) {
            for (let item in reloadedExtraction.skuFilters) {
                // BrandId & SubBrandId will be considered as one api call only under BrandId
                if (item?.toLowerCase() !== subBrandIds.toLowerCase()) {
                    filtersFromReloadedExtraction.push(item);
                }
            }
        }

        let filtersFromSelectedFiltersCount: number = 0;
        if (selectedFilters) {
            let omitByresult = _.omitBy(selectedFilters, _.isNil);
            omitByresult = _.omitBy(selectedFilters, _.isEmpty);
            filtersFromSelectedFiltersCount = Object.keys(omitByresult)?.length;
        }

        allFiltersReloaded = filtersFromReloadedExtraction.length === filtersFromSelectedFiltersCount;
        return allFiltersReloaded;
    }
}
